import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  // FormControl,
  // InputLabel,
  // Select,
  // SelectChangeEvent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  RestartAlt as RestartAltIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import Slider from '@mui/material/Slider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  actionLimitsApi,
  type ActionLimitItem,
  type LimitScope,
  type TimeWindow,
  type UpdateLimitItemInput,
} from '@/api/actionLimits';


interface ActionLimitsControlProps {
  scope: LimitScope;
  /**
   * Resource identifier:
   * - project: project UUID
   * - stream: stream UUID
   * - user: optional (backend infers from current user)
   */
  resourceId?: string;
}

interface ActionLimitRow extends ActionLimitItem {
  draft_value: number | null;
  isDirty: boolean;
}

// const TIME_WINDOW_OPTIONS: { value: TimeWindow; label: string }[] = [
//   { value: 'DAY', label: 'Per day' },
//   { value: 'WEEK', label: 'Per week' },
//   { value: 'MONTH', label: 'Per month' },
//   { value: 'HOUR', label: 'Per hour' },
//   { value: 'MINUTE', label: 'Per minute' },
// ];

const computeSliderBounds = (item: ActionLimitItem): { min: number; max: number } => {
  const min = typeof item.minimum === 'number' ? item.minimum : 0;
  if (item.maximum != null) {
    return { min, max: item.maximum };
  }

  const base = item.default_value ?? (min === 0 ? 10 : min * 2);
  const max = Math.max(base * 2, min + 1);

  return { min, max };
};

const getActiveBlockUntil = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.getTime() > Date.now() ? parsed : null;
};

export const ActionLimitsControl: React.FC<ActionLimitsControlProps> = ({ scope, resourceId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();

  const [timeWindow, _setTimeWindow] = useState<TimeWindow>('DAY');
  const [rows, setRows] = useState<ActionLimitRow[]>([]);

  // Three-dot menu state: tracks which action's menu is open
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; action: string } | null>(null);

  const resourceKey = useMemo(() => {
    if (scope === 'user') {
      // Use resourceId if provided (for viewing/editing another user's limits)
      // Otherwise use 'me' (for current user's own limits)
      return resourceId ?? 'me';
    }
    return resourceId ?? 'unknown';
  }, [scope, resourceId]);

  const {
    data,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['action-limits', scope, resourceKey, timeWindow],
    queryFn: () => actionLimitsApi.getLimits({ scope, resourceId, timeWindow }),
    enabled: scope === 'user' || !!resourceId,
  });

  useEffect(() => {
    if (!data) {
      setRows([]);
      return;
    }

    const nextRows: ActionLimitRow[] = data.items.map((item) => {
      // If current_value is null, limitation is not configured (no explicit value and no default)
      const isNotConfigured = item.current_value === null;
      
      // For not configured items, use minimum as the draft value for the slider
      // For configured items (explicit or default), use the current value
      const effectiveCurrent = isNotConfigured
        ? (item.minimum ?? 0)
        : (item.current_value ?? item.minimum ?? 0);

      return {
        ...item,
        draft_value: effectiveCurrent,
        isDirty: false,
      };
    });

    setRows(nextRows);
  }, [data]);

  const hasChanges = rows.some((row) => row.isDirty);

  // const handleTimeWindowChange = (event: SelectChangeEvent<TimeWindow>) => {
  //   const value = event.target.value as TimeWindow;
  //   setTimeWindow(value);
  // };

  const handleSliderChange = (action: string, newValue: number | number[]) => {
    const numericValue = Array.isArray(newValue) ? newValue[0] : newValue;

    setRows((prev) =>
      prev.map((row) => {
        if (row.action !== action) {
          return row;
        }

        // For not configured items, compare against minimum (the displayed value)
        const isNotConfigured = row.current_value === null;
        const original = isNotConfigured
          ? (row.minimum ?? 0)
          : (row.current_value ?? row.minimum ?? 0);
        
        const isDirty = numericValue !== original;

        return {
          ...row,
          draft_value: numericValue,
          isDirty,
        };
      }),
    );
  };

  const resetChanges = () => {
    if (!data) {
      return;
    }

    const resetRows: ActionLimitRow[] = data.items.map((item) => {
      const isNotConfigured = item.current_value === null;
      const effectiveCurrent = isNotConfigured
        ? (item.minimum ?? 0)
        : (item.current_value ?? item.minimum ?? 0);

      return {
        ...item,
        draft_value: effectiveCurrent,
        isDirty: false,
      };
    });

    setRows(resetRows);
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      const items: UpdateLimitItemInput[] = rows
        .filter((row) => row.isDirty)
        .map((row) => ({
          action: row.action,
          time_window: timeWindow,
          maximum: row.draft_value,
        }));

      if (!items.length) {
        return data ?? { items: [] };
      }

      return actionLimitsApi.updateLimits({
        scope,
        resourceId,
        timeWindow,
        items,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['action-limits', scope, resourceKey, timeWindow],
      });
    },
  });

  const resetToAutomaticMutation = useMutation({
    mutationFn: async (action: string) =>
      actionLimitsApi.resetToAutomatic({ scope, resourceId, action, timeWindow }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['action-limits', scope, resourceKey, timeWindow],
      });
      setMenuAnchor(null);
    },
  });

  const clearBlockageMutation = useMutation({
    mutationFn: async (action: string) => {
      return actionLimitsApi.clearBlockage({ scope, resourceId, action, timeWindow });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['action-limits', scope, resourceKey, timeWindow],
      });
      setMenuAnchor(null);
    },
  });

  const isBusy = isLoading || isFetching || updateMutation.isPending;

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            mb: 3,
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Maximum number of actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure how many actions can run in each time window for this{' '}
              {scope}.
            </Typography>
          </Box>

          {/* <FormControl
            size="small"
            sx={{ minWidth: 180, alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            <InputLabel id="time-window-label">Time window</InputLabel>
            <Select<TimeWindow>
              labelId="time-window-label"
              label="Time window"
              value={timeWindow}
              onChange={handleTimeWindowChange}
            >
              {TIME_WINDOW_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(error as Error)?.message || 'Failed to load limits'}
          </Alert>
        )}

        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No actions available for limits configuration.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {rows.map((row) => {
              const { min, max } = computeSliderBounds(row);
              const isNotConfigured = row.current_value === null;
              const effectiveValue =
                row.draft_value ??
                row.current_value ??
                min;
              const blockedUntil = getActiveBlockUntil(row.blocked_until);
              const hasActiveBlock = !!blockedUntil;
              const canResetToAutomatic = row.is_user_set === true;
              const hasMenuOptions = canResetToAutomatic || hasActiveBlock;
              const isMenuOpen = menuAnchor?.action === row.action;
              const isRowBusy =
                isBusy ||
                (resetToAutomaticMutation.isPending && menuAnchor?.action === row.action) ||
                (clearBlockageMutation.isPending && menuAnchor?.action === row.action);

              return (
                <Box
                  key={row.action}
                  sx={{
                    display: 'flex',
                    flexDirection: {
                      xs: 'column',
                      md: 'row',
                    },
                    alignItems: {
                      xs: 'flex-start',
                      md: 'center',
                    },
                    gap: 2,
                  }}
                >
                  <Box sx={{ minWidth: { md: 220 }, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {row.label}
                      </Typography>
                      {row.used === null ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}
                        >
                          {isNotConfigured
                            ? 'Usage not tracked (not configured)'
                            : 'Usage not tracked (using default)'}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: 'block' }}
                        >
                          Used in this window: {row.used}
                        </Typography>
                      )}
                      {hasActiveBlock && (
                        <Box sx={{ mt: 0.75 }}>
                          <Typography
                            variant="caption"
                            color="warning.main"
                            sx={{ display: 'block', fontWeight: 600 }}
                          >
                            Blocked until {blockedUntil!.toLocaleString()}
                          </Typography>
                          {row.blocking_reason && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              Reason: {row.blocking_reason}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {hasMenuOptions && (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => setMenuAnchor({ el: e.currentTarget, action: row.action })}
                          disabled={isRowBusy}
                          sx={{ mt: -0.25, flexShrink: 0 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                          anchorEl={isMenuOpen ? menuAnchor!.el : undefined}
                          open={isMenuOpen}
                          onClose={() => setMenuAnchor(null)}
                        >
                          {canResetToAutomatic && (
                            <MenuItem
                              onClick={() => resetToAutomaticMutation.mutate(row.action)}
                              disabled={resetToAutomaticMutation.isPending}
                            >
                              <RestartAltIcon fontSize="small" sx={{ mr: 1 }} />
                              Reset to automatic
                            </MenuItem>
                          )}
                          {hasActiveBlock && (
                            <MenuItem
                              onClick={() => clearBlockageMutation.mutate(row.action)}
                              disabled={clearBlockageMutation.isPending}
                            >
                              <LockOpenIcon fontSize="small" sx={{ mr: 1 }} />
                              Clear blockage
                            </MenuItem>
                          )}
                        </Menu>
                      </>
                    )}
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Min: {min}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isNotConfigured && !row.isDirty && (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            sx={{ fontWeight: 500 }}
                          >
                            Not configured
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          Max: {max}
                        </Typography>
                      </Box>
                    </Box>

                    <Slider
                      value={effectiveValue}
                      onChange={(_, value) => handleSliderChange(row.action, value as number)}
                      min={min}
                      max={max}
                      step={1}
                      valueLabelDisplay="on"
                      disabled={isBusy}
                      sx={{
                        mt: isMobile ? 1 : 0,
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>

      {hasChanges && (
        <CardActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3 }}>
          <Button
            variant="outlined"
            onClick={resetChanges}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}
            sx={{ ml: 1 }}
          >
            {updateMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Save changes'
            )}
          </Button>
        </CardActions>
      )}
    </Card>
  );
};


